import React, { useState, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, CircularProgress, Button, Typography, Box } from '@mui/material';
import SplitButton from 'components/common/buttons/SplitButton';
import { Replay, Bookmarks, BookmarkBorder, Error } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { emptyString } from 'constants/defaultValues';
import QuotationDetailComponent from './QuotationDetailComponent';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { IconButton } from '@totalsoft/rocket-ui';
import { ThemedWrapper } from 'components/common/SurfaceStyles';
import { QuotationCreatedByContainer, QuotationInsurerContainer, QuotationText } from './ComparatorStyles';

const ComparatorItemComponent = ({ quotation, onSelectQuotation, onRetryQuotation, computing, offerInNegotiation }) => {
  const { t } = useTranslation();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToRecomputeQuotation = useAuthorization(AuthorizationCodes.hasRightToRecomputeQuotation, documentTypeId, documentStateId);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const details = useMemo(() => {
    const result = [];
    const withDirectCompensation = [false, true];
    withDirectCompensation.forEach(value => {
      const detail = quotation.details?.find(x => x.isDirectCompensation === value);
      if (detail)
        result.push({
          ...detail,
          documentId: quotation.documentId,
          period: quotation.period,
          sourceOfferId: quotation.sourceOfferId,
          currencyCode: quotation.currencyCode
        });
    });
    return result;
  }, [quotation]);

  const fontWeight = useMemo(() => (details.some(d => d.selected === true) ? 'fontWeightBold' : 'fontWeightRegular'), [details]);

  const handleRetryQuotation = useCallback(
    () => onRetryQuotation(quotation.offerId, quotation.insurerId, quotation.period),
    [onRetryQuotation, quotation]
  );

  const toggleShowAdditionalInfo = useCallback(() => setShowAdditionalInfo(prev => !prev), []);

  return (
    <Grid container item alignItems='center' spacing={2}>
      <QuotationInsurerContainer item container md={4} lg={2}>
        {quotation.sourceOfferId ? <Bookmarks color='secondary' /> : <BookmarkBorder />}
        <QuotationText component={'span'} fontWeight={fontWeight} title={quotation.insurerName}>
          {quotation.insurerName}
        </QuotationText>
      </QuotationInsurerContainer>
      <QuotationCreatedByContainer item md={8} lg={4}>
        <QuotationText
          component={'span'}
          fontWeight={fontWeight}
          title={`${t('Offer.Edit.Comparator.RequestedBy')} ${t('Offer.Edit.Comparator.At')} ${t('DATE', {
            date: quotation?.offerDate
          })} ${new Date(quotation?.offerDate).toLocaleTimeString()}`}
        >
          {t('Offer.Edit.Comparator.RequestedBy')} <strong>{quotation?.userName}</strong> {''}
          {t('Offer.Edit.Comparator.At')} {''}
          <strong>{`${t('DATE', { date: quotation?.offerDate })} ${new Date(quotation?.offerDate).toLocaleTimeString()}`}</strong>
        </QuotationText>
      </QuotationCreatedByContainer>
      <Grid container item lg={6} spacing={1} alignItems={'center'}>
        {quotation.runningYN ? (
          <Grid container item md={10} spacing={2}>
            {Array(2)
              .fill(emptyString)
              .map((_, idx) => (
                <Grid item xs={6} key={`${quotation.offerId}_${idx}`}>
                  <SplitButton>
                    <Button id={`Comparator_ProgressButton_${quotation.offerId}_${idx}`} fullWidth color='primary' disabled={true}>
                      <span>{'-'}</span>
                      <CircularProgress size={24} />
                    </Button>
                  </SplitButton>
                </Grid>
              ))}
          </Grid>
        ) : (
          <Grid container item md={10} spacing={2} justifyContent='flex-end'>
            {details.map(detail => (
              <QuotationDetailComponent
                key={detail.offerDetailId}
                data={detail}
                onSelectQuotation={onSelectQuotation}
                globalReadOnly={globalReadOnly}
                computing={computing}
                offerInNegotiation={offerInNegotiation}
              />
            ))}
          </Grid>
        )}
        <Grid container item md={2} spacing={1} justifyContent='flex-end' alignItems='center' wrap={'nowrap'}>
          {quotation.additionalInfo ? (
            <IconButton
              id={`Comparator_ErrorIconButton_${quotation.offerId}`}
              tooltip={quotation.additionalInfo || emptyString}
              size={'tiny'}
              variant='text'
              onClick={toggleShowAdditionalInfo}
            >
              <Error color={'error'} />
            </IconButton>
          ) : (
            <></>
          )}
          {!quotation.sourceOfferId ? (
            <IconButton
              id={`Comparator_RetryIconButton_${quotation.offerId}`}
              size={'tiny'}
              variant='text'
              tooltip={t('Tooltips.Retry')}
              disabled={
                quotation.runningYN ||
                globalReadOnly({
                  controlName: 'Comparator.RetryRCAQuotation',
                  hasAuthorization: hasRightToRecomputeQuotation
                })
              }
              color='primary'
              onClick={handleRetryQuotation}
            >
              <Replay />
            </IconButton>
          ) : null}
        </Grid>
      </Grid>
      {showAdditionalInfo && (
        <ThemedWrapper color='error' ml={2} mt={1}>
          <Typography variant='caption'>{quotation.additionalInfo}</Typography>
        </ThemedWrapper>
      )}
      {quotation.insurerMessages && (
        <ThemedWrapper ml={2} mt={1}>
          <Typography variant='caption'>
            <Box fontStyle='italic'>{quotation.insurerMessages}</Box>
          </Typography>
        </ThemedWrapper>
      )}
    </Grid>
  );
};

ComparatorItemComponent.propTypes = {
  quotation: PropTypes.object.isRequired,
  onSelectQuotation: PropTypes.func.isRequired,
  onRetryQuotation: PropTypes.func,
  computing: PropTypes.bool.isRequired,
  offerInNegotiation: PropTypes.bool.isRequired
};

export default ComparatorItemComponent;
